/**
 * DaForge Release Hub — átmeneti teszt-hub a DaForge (Petiék rendszere) kiadásaihoz.
 *
 * A tartalom a DaForge-kontraktus JSON-alakja: `release` · `changes[CH-xxx]` · `cases[TC-xxx]`.
 * Ez az alak az, amit a DaForge később átvesz — a felület itt átmeneti, a forma nem.
 *
 * A pipák a MEGLÉVŐ `test_results` táblába mennek, séma-változás nélkül:
 *   version      = `daforge-<release.id>`   (a DTO `MaxLength(20)` — l. `daforgeVersion`)
 *   featureIndex = a TC-azonosító SZÁMA    (TC-017 → 17), NEM a listabeli pozíció ⇒
 *                  egy közbeszúrt teszteset nem csúsztatja el a már leadott eredményeket.
 */
import { daforgeRelease20260926 } from './daforge/release-2026-09-26';
import type { TestFeature } from './release-tests-data';

export interface DaforgeChange {
  id: string;
  prs: number[];
  date: string;
  area: string;
  kind: string;
  audience: string;
  title: string;
  summary: string;
  uncertain: boolean;
  note: string | null;
}

export interface DaforgeCase {
  id: string;
  change: string;
  title: string;
  role: string;
  pre: string;
  steps: string[];
  expected: string;
  /** A DaForge-oldal útvonala, ahol tesztelni kell (link nélkül — a környezet nem tisztázott). */
  path?: string | undefined;
  order?: number | undefined;
}

export interface DaforgeRelease {
  release: { id: string; title: string; from: string; to: string; status: string };
  changes: DaforgeChange[];
  cases: DaforgeCase[];
}

/** A `belső` célközönségű változás alapból rejtett — kliens-tesztelőnek nem szól. */
export const INTERNAL_AUDIENCE = 'belső';

/** Újabb kiadás elöl. */
export const daforgeReleases: DaforgeRelease[] = [daforgeRelease20260926];

export { daforgeVersion } from '../utils/daforge-version';

/** TC-017 → 17. Érvénytelen azonosítóra kivételt dob — a néma 0/NaN ütközést okozna a tárolásban. */
export function caseIndex(caseId: string): number {
  const m = /^TC-(\d+)$/.exec(caseId);
  if (!m) throw new Error(`Érvénytelen teszteset-azonosító: ${caseId}`);
  return Number(m[1]);
}

/**
 * A DaForge-tesztesetet a meglévő tesztkártya alakjára képezi. A tartalom csak magyar,
 * ezért minden nyelvi mező a magyar szöveget kapja (az `en` a kártya kötelező fallbackje).
 * Az előfeltétel az első lépés elé kerül, hogy a tesztelő a kártyán lássa.
 */
export function caseToFeature(c: DaforgeCase, change: DaforgeChange): TestFeature {
  const steps = c.pre ? [`Előfeltétel: ${c.pre}`, ...c.steps] : c.steps;
  return {
    nameEn: c.title,
    nameHu: c.title,
    featureId: c.id,
    module: `${change.area} · ${c.role}`,
    pathHint: c.path,
    steps: steps.map(s => ({ en: s, hu: s })),
    expected: { en: c.expected, hu: c.expected },
  };
}
